import { sortFactoryByField } from '@core/tools/sort.pipe';
import { NamedResource } from '@core/resources/resource';
import { Category, CategoryIO } from '../category/category';
import { CategorySerializer } from '../category/category.serializer';
import { DESTINATION_ICONS, DestinationType, Destination } from '../destination';
import { LANGUAGE_I18N_2_THEME } from '../language.maps';

export interface ThemeLanguage {
  language: string;
  label: string;
  badge: string;
}

/**
 * Interface describing a Theme object as returned by the API
 */
export interface ThemeIO {
  id: string;
  languages: ThemeLanguage[];
  categories: CategoryIO[];
}

export function isTheme(arg: Destination): arg is Theme {
  return arg instanceof Theme;
}

export class Theme extends NamedResource implements Destination<Theme> {
  private _lang: string;
  private _selected: Category[] = [];
  private _filtered: Category[] = [];
  private _langMap = LANGUAGE_I18N_2_THEME;
  readonly type: DestinationType = 'theme';
  readonly icon = DESTINATION_ICONS.theme;

  badge: string;
  warning: string[] = [];

  /**
   * List of categories included in this theme.
   */
  readonly categories: Category[];

  constructor(json: ThemeIO, lang: string) {
    super();
    this._lang = lang;
    const catSerializer = new CategorySerializer(this._lang);
    this._initTheme(json);
    const sortFn = sortFactoryByField<Category>('name');
    this.categories = json.categories.map((cat: CategoryIO) => catSerializer.fromJson(cat)).sort(sortFn);
    this._filtered = this.categories;
  }

  private _getLocale(json: ThemeIO): ThemeLanguage | undefined {
    return json.languages.find((loc: ThemeLanguage) => loc.language === this._langMap[this._lang]);
  }

  private _initTheme(json: ThemeIO): void {
    const locale = this._getLocale(json);
    this.id = json.id.toString();
    this.name = locale?.label ?? 'No label';
    this.badge = locale?.badge ?? '';
  }

  private _filterPredicate(search: string): (cat: Category) => boolean {
    return (cat: Category): boolean => {
      const searchString = search.trim().toLowerCase();
      return search.length ? cat.name.toLowerCase().includes(searchString) || cat.description.toLowerCase().includes(searchString) || cat.subtitle.toLowerCase().includes(searchString) : true;
    };
  }

  /**
   * Returns the users's selected categories
   */
  get selected(): Category[] {
    return this._selected;
  }

  /**
   * Returns the filtered categories
   */
  get filtered(): Category[] {
    return this._filtered;
  }

  /**
   * Returns the length of user's selected categories
   */
  get length(): number {
    return this._selected.length;
  }

  /**
   * Returns the length of filtered categories
   */
  get filteredLength(): number {
    return this._filtered.length;
  }

  get count(): string {
    return `${this.length}/${this.filteredLength}`;
  }

  /**
   * Returns an array of user's selected categories's id
   */
  get payload(): string[] {
    return this._selected.map((cat: Category) => cat.id);
  }

  getFullName(): string {
    return `${this.name}`;
  }

  getCount(): string {
    return `${this.length}/${this.categories.length}`;
  }

  clone(): Theme {
    const clone = new Theme(this.toJson(), this._lang);
    clone.selectAll(this.selected);
    return clone;
  }

  toJson(): ThemeIO {
    return {
      id: this.id,
      languages: [{language: this._langMap[this._lang], label: this.name, badge: this.badge}],
      categories: this.categories.map((cat: Category) => cat.toJson())
    };
  }

  /**
   * Select a theme's category
   */
  select(cat: Category): Theme {
    // if category is not already selected, and if category belongs to current theme
    if (!this._selected.some((item: Category) => item.id === cat.id) && this.categories.some((item: Category) => item.id === cat.id)) {
      this._selected.push(cat);
    }

    return this;
  }

  /**
   * If an array of categories is provided, select those categories. Otherwise, select all categories from the current theme.
   */
  selectAll(cat?: Category[]): Theme {
    const list: Category[] = cat ?? this.categories;
    list.forEach((item: Category) => {
      this.select(item);
    });
    return this;
  }

  /**
   * Remove a category form user's selected categories list
   */
  unselect(cat: Category): Theme {
    const index = this._selected.findIndex((item: Category) => item.id === cat.id);
    this._selected.splice(index, 1);
    return this;
  }

  /**
   * Remove all selected categories
   */
  clear(): Theme {
    this._selected = [];
    return this;
  }

  filter(search?: string): void {
    this._filtered = search ? this.categories.filter(this._filterPredicate(search)) : this.categories;
  }


  setWarning(payload: string): Theme {
    if (!this.warning.includes(payload)) {
      this.warning.push(payload);
    }
    return this;
  }

  setWarningOnSelected(payload: string): Theme {
    this.selected.forEach((cat: Category) => cat.setWarning(payload));
    return this;
  }

  clearWarning(): Theme {
    this.warning = [];
    return this;
  }

  clearAllWarnings(): Theme {
    this.selected.forEach((cat: Category) => cat.clearWarning());
    this.warning = [];
    return this;
  }
}
